import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions, handlePermissionClick } from '../../hooks/usePermissions';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, PowerOff, Users, LayoutGrid, List, Search, ContactRound, RotateCcw, Eye } from 'lucide-react';
import { ResponsavelFichaDesktop, ResponsavelFichaFooter } from './ResponsavelFichaDesktop';
import {
  ResponsavelFormModal,
  RESPONSAVEL_PARENTESCOS,
  type ResponsavelFormModel,
} from './ResponsavelFormModal';
import {
  AppStatusBadge,
  getResponsavelAppStatus,
  type AppAcessoStatusKey,
} from './ResponsavelAcessoApp';
import { InviteResultDialog } from '../../components/invite/InviteResultDialog';
import { toast } from 'sonner';
import { useCrud } from '../../api/hooks/useCrud';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/authStore';
import { DataToolbar } from '../../components/ui/DataToolbar';
import { DataTable, PaginationFooter } from '../../components/ui/DataTable';
import { Modal } from '../../components/ui/Modal';
import { ConfirmModal } from '../../components/ui/ConfirmModal';
import { SetupPageHero } from '../../components/onboarding/SetupPageHero';
import { ListAvatar } from '../../components/admin/list-avatar';
import '../../components/ui/datatable.css';
import { ROUTES } from '../../api/routes';

interface AlunoVinculado {
  aluno: {
    id: string;
    nome: string;
    matricula?: string;
    fotoUrl?: string | null;
    turma?: { id: string; nome: string; serie?: string; turno?: string } | null;
  };
  grauParentesco?: string;
  principal?: boolean;
}

interface UsuarioAuditoria {
  id: string;
  nome: string;
}

interface Responsavel {
  id: string; nome: string; email: string; telefone: string;
  fotoUrl?: string | null;
  cpf?: string; parentesco?: string; dataNascimento?: string; ativo: boolean;
  usuarioId?: string | null;
  usuario?: {
    id: string;
    statusConta?: string | null;
    tokenExpiraEm?: string | null;
    ativadoEm?: string | null;
  } | null;
  _count?: { alunos: number };
  criadoEm: string;
  atualizadoEm?: string;
  criadoPor?: UsuarioAuditoria | null;
  atualizadoPor?: UsuarioAuditoria | null;
  alunos?: AlunoVinculado[];
}

export function ResponsaveisPage() {
  const { canManage: canManageFn, canView: canViewFn } = usePermissions();
  const canManage = canManageFn('responsaveis');
  const canView = canViewFn('responsaveis') || canManage;

  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const escolaId = user?.escolaId;
  const { data: responsaveis, isLoading, error: loadError, deactivate, refetch } = useCrud<Responsavel>('responsaveis');

  useEffect(() => {
    if (loadError) {
      toast.error('Não foi possível carregar os responsáveis. Tente atualizar a página.');
    }
  }, [loadError]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<Responsavel | null>(null);
  const [editing, setEditing] = useState<Responsavel | null>(null);
  const [filterParentesco, setFilterParentesco] = useState('');
  const [filterApp, setFilterApp] = useState<'' | AppAcessoStatusKey>('');
  const [searchQuery, setSearchQuery] = useState('');

  const [fichaOpen, setFichaOpen] = useState(false);
  const [fichaResponsavel, setFichaResponsavel] = useState<Responsavel | null>(null);
  const [fichaDefaultTab, setFichaDefaultTab] = useState<'filhos' | 'contato' | 'app'>('filhos');
  const [inviteResult, setInviteResult] = useState<{
    open: boolean;
    nome: string;
    email: string;
    activationLink: string;
    whatsappLink?: string | null;
    emailSent?: boolean;
    emailError?: string | null;
  } | null>(null);
  const [viewMode, setViewMode] = useState<'lista' | 'cards'>('lista');
  const [pageSize, setPageSize] = useState(20);

  const openNew = () => {
    setEditing(null);
    setModalOpen(true);
  };

  /** Abre a ficha de visualização completa */
  const openFicha = (r: Responsavel, initialTab: 'filhos' | 'contato' | 'app' = 'filhos') => {
    setFichaDefaultTab(initialTab);
    setFichaResponsavel(r);
    setFichaOpen(true);
  };

  /** Da ficha → editar: fecha ficha e abre o modal de edição */
  const fichaToEdit = () => {
    if (!fichaResponsavel) return;
    setFichaOpen(false);
    openEdit(fichaResponsavel);
  };

  /** Da ficha → desativar */
  const fichaToDeactivate = () => {
    if (!fichaResponsavel) return;
    setFichaOpen(false);
    requestDelete(fichaResponsavel);
  };

  /** Da ficha → reativar */
  const fichaToReactivate = async () => {
    if (!fichaResponsavel) return;
    setFichaOpen(false);
    await handleReactivate(fichaResponsavel);
  };

  const openEdit = (r: Responsavel) => {
    setEditing(r);
    setModalOpen(true);
  };

  const handleFormSaved = async (result: {
    id: string;
    created: boolean;
    responsavel?: any;
  }) => {
    setModalOpen(false);
    await refetch();
    window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    if (result.created && result.responsavel) {
      const resp = result.responsavel;
      if (resp.activationLink) {
        setInviteResult({
          open: true,
          nome: resp.nome,
          email: resp.email || '',
          activationLink: resp.activationLink,
          whatsappLink: resp.whatsappLink,
          emailSent: resp.emailEnviado,
          emailError: resp.emailError,
        });
      }
    }
  };

  /** Verifica se o responsável pode ser desativado (regra #16 — Remoção Protegida) */
  const canDeactivate = (r: Responsavel): { ok: boolean; reason?: string } => {
    const alunoCount = r._count?.alunos ?? r.alunos?.length ?? 0;
    if (alunoCount > 0) {
      return { ok: false, reason: `Não é possível desativar. ${alunoCount} aluno(s) vinculado(s). Remova os vínculos primeiro.` };
    }
    if (r.usuarioId) {
      return { ok: false, reason: 'Não é possível desativar. Conta de usuário ativa vinculada.' };
    }
    return { ok: true };
  };

  const requestDelete = (r: Responsavel) => {
    const check = canDeactivate(r);
    if (!check.ok) {
      toast.warning(check.reason!);
      return;
    }
    setDeleteConfirm(r);
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    const id = deleteConfirm.id;
    setDeleteConfirm(null);
    try {
      await deactivate.mutateAsync(id);
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
      toast.success('Responsável desativado');
    } catch {
      toast.error('Erro ao desativar');
    }
  };

  const handleReactivate = async (r: Responsavel) => {
    try {
      await api.patch(ROUTES.responsaveis.reativar(escolaId!, r.id));
      toast.success(`"${r.nome}" reativado com sucesso!`);
      refetch();
      window.dispatchEvent(new CustomEvent('ac-data-refetch'));
    } catch {
      toast.error('Erro ao reativar responsável.');
    }
  };

  const filtered = useMemo(() => {
    let list = responsaveis;
    if (filterParentesco) list = list.filter(r => r.parentesco === filterParentesco);
    if (filterApp) {
      list = list.filter((r) => getResponsavelAppStatus(r).key === filterApp);
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((r) =>
      [r.nome, r.email, r.telefone, r.parentesco]
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [responsaveis, filterParentesco, filterApp, searchQuery]);

  const appCounts = useMemo(() => {
    const c = { sem_conta: 0, pendente: 0, expirado: 0, ativa: 0, suspensa: 0 };
    for (const r of responsaveis) {
      const k = getResponsavelAppStatus(r).key;
      c[k] += 1;
    }
    return c;
  }, [responsaveis]);



  const parentescoPill = (p?: string) => {
    if (!p) return '—';
    const colors: Record<string, string> = { 'Pai': '#2563eb', 'Mãe': '#ec4899', 'Avô/Avó': '#7c3aed', 'Tio/Tia': '#059669', 'Outro': '#6b7280' };
    return (
      <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: 5, fontWeight: 600, background: `${colors[p] || '#6b7280'}15`, color: colors[p] || '#6b7280' }}>{p}</span>
    );
  };

  const columns: ColumnDef<Responsavel>[] = [
    {
      accessorKey: 'nome',
      header: 'Nome',
      cell: ({ row }) => (
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 10, opacity: row.original.ativo ? 1 : 0.4 }}>
          <ListAvatar fotoUrl={row.original.fotoUrl} nome={row.original.nome} role="RESPONSAVEL" size={44} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 700, fontSize: '.92rem', color: 'var(--text-main)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.original.nome}
            </div>
            <div style={{ fontSize: '.76rem', color: '#2563eb', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {row.original.email || '—'}
            </div>
          </div>
        </div>
      ),
    },
    { accessorKey: 'telefone', header: 'Telefone', cell: ({ row }) => <span style={{ opacity: row.original.ativo ? 1 : 0.4 }}>{row.original.telefone || '—'}</span> },
    {
      accessorKey: 'parentesco', header: 'Parentesco',
      cell: ({ row }) => parentescoPill(row.original.parentesco),
    },
    {
      id: 'alunos', header: 'Alunos',
      cell: ({ row }) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: (row.original._count?.alunos ?? 0) > 0 ? '#2563eb' : '#9ca3af' }}>
          <Users size={14} /> {row.original._count?.alunos ?? '—'}
        </span>
      ),
    },
    {
      id: 'app',
      header: 'App',
      enableSorting: false,
      cell: ({ row }) => <AppStatusBadge status={getResponsavelAppStatus(row.original)} />,
    },
    {
      accessorKey: 'ativo', header: 'Cadastro',
      cell: ({ row }) => <span className={`badge ${row.original.ativo ? 'badge-active' : 'badge-inactive'}`}>{row.original.ativo ? 'Ativo' : 'Inativo'}</span>,
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            className="action-btn action-btn-view"
            title="Ver Detalhes"
            onClick={() => openFicha(row.original)}
          >
            <Eye size={15} />
          </button>
          <button
            type="button"
            className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
            title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
            onClick={() => handlePermissionClick(canManage, () => openEdit(row.original), 'Você não tem permissão para editar responsáveis.')}
          >
            <Pencil size={15} />
          </button>
          {row.original.ativo ? (
            <button
              type="button"
              className={`action-btn ${!canManage || !canDeactivate(row.original).ok ? 'opacity-40 cursor-not-allowed' : 'action-btn-danger'}`}
              title={!canManage ? 'Permissão restrita para desativação' : (canDeactivate(row.original).ok ? 'Desativar' : canDeactivate(row.original).reason)}
              onClick={() => {
                if (!canDeactivate(row.original).ok) {
                  toast.warning(canDeactivate(row.original).reason!);
                  return;
                }
                handlePermissionClick(canManage, () => requestDelete(row.original), 'Você não tem permissão para desativar responsáveis.');
              }}
            >
              <PowerOff size={15} />
            </button>
          ) : (
            <button
              type="button"
              className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : 'action-btn-success'}`}
              title={!canManage ? 'Permissão restrita para reativação' : 'Reativar'}
              onClick={() => handlePermissionClick(canManage, () => handleReactivate(row.original), 'Você não tem permissão para reativar responsáveis.')}
            >
              <RotateCcw size={15} />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="admin-page" style={{ marginTop: -24, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {!isLoading && (
        <SetupPageHero
          icon={ContactRound}
          etapa={6}
          titulo="Responsáveis"
          descricao={
            canManage
              ? 'Consulte famílias, edite cadastros e gerencie o acesso ao app (convite e recuperação).'
              : 'Consulte responsáveis, vínculos com alunos e status da conta no aplicativo.'
          }
          compact
        />
      )}
      <div className="ac-admin-data-card" style={{ background: 'var(--bg-card, white)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12, overflow: 'hidden', display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <DataToolbar
          busca={searchQuery}
          onBuscaChange={setSearchQuery}
          placeholder="Buscar responsável..."
          filters={
            <>
              <select
                value={filterParentesco}
                onChange={(e) => setFilterParentesco(e.target.value)}
                title="Filtrar por parentesco"
                style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px', fontSize: '.8rem', background: 'var(--bg-card)', color: 'var(--text-main)', flexShrink: 0 }}
              >
                <option value="">Todos os parentescos</option>
                {RESPONSAVEL_PARENTESCOS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select
                value={filterApp}
                onChange={(e) => setFilterApp(e.target.value as '' | AppAcessoStatusKey)}
                title="Filtrar por acesso ao app"
                style={{ height: 34, borderRadius: 8, border: '1px solid var(--border-color, #e5e7eb)', padding: '0 10px', fontSize: '.8rem', background: 'var(--bg-card)', color: 'var(--text-main)', flexShrink: 0 }}
              >
                <option value="">App: todos</option>
                <option value="sem_conta">Sem conta ({appCounts.sem_conta})</option>
                <option value="pendente">Convite pendente ({appCounts.pendente})</option>
                <option value="expirado">Convite expirado ({appCounts.expirado})</option>
                <option value="ativa">App ativo ({appCounts.ativa})</option>
                <option value="suspensa">Suspensa ({appCounts.suspensa})</option>
              </select>
            </>
          }
          actions={
            <button
              type="button"
              className={`btn-primary ${!canManage ? 'opacity-50 cursor-not-allowed filter grayscale' : ''}`}
              onClick={() => handlePermissionClick(canManage, openNew, 'Você não tem permissão para cadastrar responsáveis.')}
              title={!canManage ? 'Permissão restrita a Diretores e Administradores' : 'Cadastrar novo responsável'}
              style={{ height: 34, padding: '0 12px', fontSize: '.8rem', display: 'inline-flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              <Plus size={15} /> Cadastrar Responsável
            </button>
          }
          totalRecords={filtered.length}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
          pageSizeOptions={[10, 20, 40, 100]}
          viewMode={viewMode}
          onViewModeChange={(m) => setViewMode(m as 'lista' | 'cards')}
          viewModes={['lista', 'cards']}
        />
        <div className="ac-admin-list-scroll" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflowY: 'auto', minHeight: 0 }}>
          {viewMode === 'lista' ? (
            <DataTable columns={columns} data={filtered} isLoading={isLoading} searchPlaceholder="Buscar responsável..." showToolbar={false} pageSize={pageSize} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-2 sm:gap-3 p-2 sm:p-3">
              {filtered.map((r) => (
                <div key={r.id} className="border border-[var(--border-color,#e5e7eb)] rounded-xl p-2.5 sm:p-3 bg-[var(--bg-card,white)] shadow-sm hover:shadow transition-shadow flex flex-col justify-between">
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6, gap: 8, alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                      <ListAvatar fotoUrl={r.fotoUrl} nome={r.nome} role="RESPONSAVEL" size={40} />
                      <div style={{ minWidth: 0 }}>
                        <div className="font-bold text-xs sm:text-sm text-[var(--text-main,#1e293b)] truncate">{r.nome}</div>
                        <div className="text-[11px] text-blue-600 dark:text-blue-400 truncate">{r.email || '—'}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end', flexShrink: 0 }}>
                      <span className={`badge ${r.ativo ? 'badge-active' : 'badge-inactive'} text-[9px] px-1.5 py-0.5`}>{r.ativo ? 'Ativo' : 'Inativo'}</span>
                      <div className="scale-90 origin-right">
                        <AppStatusBadge status={getResponsavelAppStatus(r)} />
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mb-2 truncate">
                    {r.telefone || '—'} • {parentescoPill(r.parentesco)} • <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, verticalAlign: 'middle' }}><Users size={11} /> {r._count?.alunos ?? 0} aluno(s)</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4, borderTop: '1px solid var(--border-color,#e5e7eb)/50', paddingTop: 6 }}>
                    <button type="button" className="action-btn action-btn-view" title="Consultar ficha" onClick={() => openFicha(r)}>
                      <Eye size={13} />
                    </button>
                    <button
                      type="button"
                      className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : ''}`}
                      title={!canManage ? 'Permissão restrita para edição' : 'Editar'}
                      onClick={() => handlePermissionClick(canManage, () => openEdit(r), 'Você não tem permissão para editar responsáveis.')}
                    >
                      <Pencil size={13} />
                    </button>
                    {r.ativo ? (
                      <button
                        type="button"
                        className={`action-btn ${!canManage || !canDeactivate(r).ok ? 'opacity-40 cursor-not-allowed' : 'action-btn-danger'}`}
                        title={!canManage ? 'Permissão restrita para desativação' : (canDeactivate(r).ok ? 'Desativar' : canDeactivate(r).reason)}
                        onClick={() => {
                          if (!canDeactivate(r).ok) {
                            toast.warning(canDeactivate(r).reason!);
                            return;
                          }
                          handlePermissionClick(canManage, () => requestDelete(r), 'Você não tem permissão para desativar responsáveis.');
                        }}
                      >
                        <PowerOff size={13} />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={`action-btn ${!canManage ? 'opacity-40 cursor-not-allowed' : 'action-btn-success'}`}
                        title={!canManage ? 'Permissão restrita para reativação' : 'Reativar'}
                        onClick={() => handlePermissionClick(canManage, () => handleReactivate(r), 'Você não tem permissão para reativar responsáveis.')}
                      >
                        <RotateCcw size={13} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {filtered.length === 0 && <div style={{ color: 'var(--text-muted)', fontSize: '.85rem', padding: 8 }}>Nenhum registro encontrado.</div>}
            </div>
          )}
          {viewMode === 'cards' && (
            <PaginationFooter
              totalItems={filtered.length}
              pageSize={pageSize}
            />
          )}
        </div>

      </div>

      {escolaId && (
        <ResponsavelFormModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          escolaId={escolaId}
          responsavel={editing}
          onSaved={(result) => void handleFormSaved(result)}
          onMatricularNovoAluno={(resp) => {
            setModalOpen(false);
            // Determina a rota base do painel (admin, municipal, etc.)
            const pathSegments = window.location.pathname.split('/');
            const panelType = pathSegments[1] || 'admin';
            navigate(`/${panelType}/alunos`, {
              state: {
                prefilledResponsavel: {
                  id: resp.id,
                  nome: resp.nome,
                  email: resp.email || '',
                  telefone: resp.telefone || '',
                  cpf: resp.cpf || '',
                  dataNascimento: resp.dataNascimento ? new Date(resp.dataNascimento).toISOString().slice(0, 10) : '',
                  parentesco: resp.parentesco || 'PAI',
                  principal: true,
                }
              }
            });
          }}
        />
      )}

      {/* === Ficha desktop (visualização analítica) === */}
      <Modal
        open={fichaOpen}
        onClose={() => setFichaOpen(false)}
        title="Ficha do responsável"
        size="wide"
        contentClassName="ac-ficha-modal ac-responsavel-ficha-modal"
        hideCloseButton
        footer={
          fichaResponsavel && escolaId ? (
            <ResponsavelFichaFooter
              resp={fichaResponsavel}
              canManage={canManage}
              onEdit={fichaToEdit}
              onDeactivate={fichaToDeactivate}
              onReactivate={() => void fichaToReactivate()}
              canDeactivate={canDeactivate(fichaResponsavel)}
            />
          ) : undefined
        }
      >
        {fichaResponsavel && escolaId ? (
          <ResponsavelFichaDesktop
            responsavelId={fichaResponsavel.id}
            escolaId={escolaId}
            canManage={canManage}
            onClose={() => setFichaOpen(false)}
            onEdit={fichaToEdit}
            onDeactivate={fichaToDeactivate}
            onReactivate={() => void fichaToReactivate()}
            canDeactivate={canDeactivate(fichaResponsavel)}
            onInvited={() => void refetch()}
            defaultTab={fichaDefaultTab}
          />
        ) : null}
      </Modal>

      <ConfirmModal
        open={!!deleteConfirm}
        title="Desativar responsável"
        message={
          deleteConfirm
            ? `Deseja desativar "${deleteConfirm.nome}"? O responsável ficará com status inativo na listagem.`
            : ''
        }
        confirmLabel="Desativar"
        cancelLabel="Cancelar"
        variant="danger"
        onCancel={() => setDeleteConfirm(null)}
        onConfirm={() => void confirmDelete()}
      />

      {inviteResult && (
        <InviteResultDialog
          open={inviteResult.open}
          nome={inviteResult.nome}
          email={inviteResult.email}
          perfil="RESPONSAVEL"
          activationLink={inviteResult.activationLink}
          whatsappLink={inviteResult.whatsappLink}
          emailSent={inviteResult.emailSent}
          emailReason={inviteResult.emailError}
          onClose={() => setInviteResult(null)}
        />
      )}
    </div>
  );
}
